function readInt(name, fallback) {
  const raw = process.env[name];
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function readFloat(name, fallback) {
  const raw = process.env[name];
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/** T-A05：异步净化等待默认上限（10 分钟），防止无限轮询 */
export const DEFAULT_UPLOAD_READY_TIMEOUT_MS = 600_000;

/** 用户上传硬上限（字节）：超过直接拦截；以下自动温和压缩。与后端 sanitize.max-bytes 对齐。 */
export const UPLOAD_MAX_BYTES = readInt('REACT_APP_IPE_UPLOAD_MAX_BYTES', 50 * 1024 * 1024);

/** 与 client/.env 中 REACT_APP_IPE_UPLOAD_* 对应，修改后需重启前端 */
export const UPLOAD_CONFIG = {
  /** 硬拦截：单张原始文件超过则拒绝（默认 50MB） */
  maxBytes: UPLOAD_MAX_BYTES,
  /**
   * 压缩最长边：与后端 sanitize max-edge 对齐 4096，避免压到 2048 发糊。
   * 仅当超过该边长才等比缩小。
   */
  maxEdgePx: readInt('REACT_APP_IPE_UPLOAD_MAX_EDGE_PX', 4096),
  /** JPEG 质量偏高，优先保清晰再控体积 */
  jpegQuality: Math.min(0.95, Math.max(0.5, readFloat('REACT_APP_IPE_UPLOAD_JPEG_QUALITY', 0.92))),
  chunkSize: readInt('REACT_APP_IPE_UPLOAD_CHUNK_SIZE', 20),
  /** multipart / 直传分片的并行批次数；默认 2（与 B2「2 批并行」一致） */
  chunkConcurrency: readInt('REACT_APP_IPE_UPLOAD_CHUNK_CONCURRENCY', 2),
  /** 单批内 OSS PUT 并发 */
  putConcurrency: readInt('REACT_APP_IPE_UPLOAD_PUT_CONCURRENCY', 4),
  /**
   * T-A13：全局压缩槽并发（默认 3，生产保守）。
   * 预发标定可试 4；勿与 PUT/CHUNK 同时盲目抬高。上限钳制 8。
   */
  compressConcurrency: Math.min(8, readInt('REACT_APP_IPE_UPLOAD_COMPRESS_CONCURRENCY', 3)),
  timeoutMs: readInt('REACT_APP_IPE_UPLOAD_TIMEOUT_MS', 120000),
  batchTimeoutMs: readInt('REACT_APP_IPE_UPLOAD_BATCH_TIMEOUT_MS', 180000),
  maxRetries: readInt('REACT_APP_IPE_UPLOAD_MAX_RETRIES', 3),
  retryBaseMs: readInt('REACT_APP_IPE_UPLOAD_RETRY_BASE_MS', 1000),
  /** B2b：尝试 OSS 直传；后端 capabilities.directOss=false（如 local）时自动回退 */
  directOss: String(process.env.REACT_APP_IPE_UPLOAD_DIRECT_OSS || 'true').toLowerCase() !== 'false',
  /** T-A05：异步净化轮询间隔（ms） */
  readyPollMs: readInt('REACT_APP_IPE_UPLOAD_READY_POLL_MS', 800),
  /** T-A05：异步净化等待超时（ms），默认 10 分钟；有界轮询，非无限 */
  readyTimeoutMs: readInt('REACT_APP_IPE_UPLOAD_READY_TIMEOUT_MS', DEFAULT_UPLOAD_READY_TIMEOUT_MS),
  /**
   * T-A11：禁止空 url 成功卡入列（默认 true）。
   * false 时仍允许「地址缺失」成功项（仅回滚/对照）。
   */
  mountStrict: String(process.env.REACT_APP_IPE_UPLOAD_MOUNT_STRICT || 'true').toLowerCase() !== 'false',
  /**
   * T-A12：批间 PUT 与 waitReady 重叠（默认 false，基线关）。
   */
  pipelineOverlap: String(process.env.REACT_APP_IPE_UPLOAD_PIPELINE_OVERLAP || 'false').toLowerCase() === 'true',
  /**
   * T-A14 / T-A19：IndexedDB 异步备份（默认 true）。
   * true = 热路径不 await IDB，OSS 与备份并行（策略 B）；失败静默，内存 File 仍可重试（C）。
   * false = 回退策略 A（await IDB 再 flush）。
   */
  idbAsync: String(process.env.REACT_APP_IPE_UPLOAD_IDB_ASYNC || 'true').toLowerCase() !== 'false',
  /**
   * T-A17：前端 Worker 池压缩（默认 false；预发灰度再开）。
   * 关或能力不足 → 主线程 compressImageForDatabase。
   */
  workerCompress: String(process.env.REACT_APP_IPE_UPLOAD_WORKER_COMPRESS || 'false').toLowerCase() === 'true',
  /**
   * T-A17：Worker 池大小 = 同时 arrayBuffer 上限（默认 4，钳制 1～8）。
   */
  workerPool: Math.min(8, Math.max(1, readInt('REACT_APP_IPE_UPLOAD_WORKER_POOL', 4))),
  /**
   * T-A18：选图乐观预览（默认 false；预发灰度再开）。
   * 点上传后立刻 blob 入列；内容 hash 回传后再撤重卡。
   */
  optimisticPreview: String(process.env.REACT_APP_IPE_UPLOAD_OPTIMISTIC_PREVIEW || 'false').toLowerCase() === 'true',
};

export default UPLOAD_CONFIG;
